"use client";

import { useEffect, useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { Sparkles, Check, X, FileText, User } from "lucide-react";

/* ── Component ─────────────────────────────────── */

export function AiAssistantDemo() {
  const [step, setStep] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

    async function runAnimation() {
      const isCancelled = () => cancelledRef.current;

      setStep(0);
      await wait(500);
      if (isCancelled()) return;

      setStep(1); // user question
      await wait(1100);
      if (isCancelled()) return;

      setStep(2); // typing dots
      await wait(1300);
      if (isCancelled()) return;

      setStep(3); // reply + citation
      await wait(2600);
      if (isCancelled()) return;

      setStep(4); // proposal card
      await wait(1800);
      if (isCancelled()) return;

      setStep(5); // approve pressed
      await wait(600);
      if (isCancelled()) return;

      setStep(6); // added
      await wait(5000);
      if (isCancelled()) return;

      if (!isCancelled()) runAnimation();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          runAnimation();
        }
      },
      { threshold: 0.4 }
    );

    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      cancelledRef.current = true;
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={containerRef}
      className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-12 px-6 pt-24 pb-12 md:flex-row-reverse lg:px-12"
    >
      {/* ── Visual Demo Container ──────────────── */}
      <div className="relative w-full max-w-2xl flex-1 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-bl from-indigo-900/20 via-background to-purple-900/10 p-4 shadow-2xl before:pointer-events-none before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] before:from-indigo-500/10 before:via-transparent before:to-transparent">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-[size:20px_20px] opacity-[0.03]" />

        <div className="relative flex h-[420px] flex-col overflow-hidden rounded-2xl border border-border bg-card/90 shadow-inner backdrop-blur-xl">
          {/* Assistant header */}
          <div className="flex items-center gap-2 border-b border-border/60 bg-muted/30 px-4 py-3">
            <div className="flex size-6 items-center justify-center rounded-lg bg-brand/15 text-brand">
              <Sparkles className="size-3.5" />
            </div>
            <span className="text-sm font-bold text-foreground">
              Writing Assistant
            </span>
            <span className="ml-auto flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-500">
              <span className="size-1.5 rounded-full bg-emerald-500" />
              Reading your story
            </span>
          </div>

          {/* Chat body */}
          <div className="flex flex-1 flex-col gap-3 overflow-hidden p-4">
            {/* User question */}
            <div
              className={cn(
                "self-end transition-all duration-500",
                step >= 1
                  ? "translate-y-0 opacity-100"
                  : "translate-y-3 opacity-0"
              )}
            >
              <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-brand px-3.5 py-2.5 text-sm text-white shadow-sm">
                What color were Elandor&apos;s eyes back in Chapter 2?
              </div>
            </div>

            {/* Assistant response block */}
            <div className="flex items-start gap-2.5">
              <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand">
                <Sparkles className="size-3.5" />
              </div>

              {/* Typing dots (step 2 only) */}
              {step === 2 && (
                <div className="flex items-center gap-1 rounded-2xl rounded-tl-sm border border-border bg-background/70 px-3.5 py-3">
                  <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.3s]" />
                  <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.15s]" />
                  <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/60" />
                </div>
              )}

              {/* Reply (step 3+) */}
              {step >= 3 && (
                <div className="max-w-[85%] space-y-2">
                  <div className="rounded-2xl rounded-tl-sm border border-border bg-background/70 px-3.5 py-2.5 text-sm leading-relaxed text-foreground shadow-sm">
                    You wrote them as{" "}
                    <span className="font-semibold text-foreground">
                      storm-grey
                    </span>{" "}
                    in Chapter 2 — but the draft you&apos;re editing now says{" "}
                    <span className="rounded bg-destructive/10 px-1 font-semibold text-destructive">
                      pale blue
                    </span>
                    . Want me to flag the mismatch?
                    {/* Citation chip */}
                    <span className="mt-2 flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-semibold text-indigo-400">
                        <FileText className="size-3" />
                        Chapter 2 · &ldquo;storm-grey eyes&rdquo;
                      </span>
                    </span>
                  </div>

                  {/* Proposal card (step 4+) */}
                  <div
                    className={cn(
                      "overflow-hidden rounded-xl border border-brand/30 bg-brand/5 transition-all duration-500",
                      step >= 4
                        ? "max-h-64 translate-y-0 opacity-100 sm:max-h-40"
                        : "max-h-0 translate-y-2 opacity-0"
                    )}
                  >
                    <div className="flex items-center gap-2 border-b border-brand/20 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-brand">
                      <Sparkles className="size-3" />
                      Suggested change
                    </div>

                    {step < 6 ? (
                      <div className="space-y-2.5 p-3">
                        <div className="flex items-center gap-2">
                          <span className="flex size-6 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-400">
                            <User className="size-3.5" />
                          </span>
                          <span className="text-xs text-foreground">
                            Add note to{" "}
                            <span className="font-semibold">Elandor</span>: eyes
                            are storm-grey
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            tabIndex={-1}
                            aria-hidden="true"
                            className={cn(
                              "flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all duration-200",
                              step === 5 &&
                                "scale-95 ring-2 ring-brand/50 ring-offset-1 ring-offset-background"
                            )}
                          >
                            <Check className="size-3.5" strokeWidth={3} />
                            Approve
                          </button>
                          <button
                            type="button"
                            tabIndex={-1}
                            aria-hidden="true"
                            className="flex items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold text-muted-foreground"
                          >
                            <X className="size-3.5" strokeWidth={3} />
                            Reject
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-3 text-xs font-semibold text-emerald-500">
                        <span className="flex size-6 items-center justify-center rounded-full bg-emerald-500/15">
                          <Check className="size-3.5" strokeWidth={3} />
                        </span>
                        Added to your Wiki
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Fake input bar */}
          <div className="border-t border-border/60 px-4 py-3">
            <div className="flex items-center gap-2 rounded-full border border-border bg-background/70 px-4 py-2 text-xs text-muted-foreground">
              Ask about your world, characters, or plot…
            </div>
          </div>
        </div>
      </div>

      {/* ── Text Explainer ─────────────────────── */}
      <div className="max-w-lg flex-1 space-y-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">
          <Sparkles className="size-3.5" />
          AI writing assistant
        </div>
        <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          An AI partner that actually knows your story
        </h2>
        <p className="text-lg leading-relaxed text-muted-foreground">
          Ask questions, catch continuity slips, and build out your world
          without leaving the page. The assistant reads your chapters and your
          wiki — then helps, without ever writing your prose for you.
        </p>

        <ul className="mt-8 space-y-4">
          <li
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 3 ? "opacity-100" : "opacity-40"
            )}
          >
            <div className="mt-1 shrink-0 rounded-full bg-indigo-500/20 p-1 text-indigo-400">
              <FileText className="size-4" />
            </div>
            <div>
              <span className="font-semibold text-foreground">
                Grounded in your work:
              </span>{" "}
              It searches your actual chapters and wiki before it answers — with
              citations.
            </div>
          </li>
          <li
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 3 ? "opacity-100" : "opacity-40"
            )}
          >
            <div className="mt-1 shrink-0 rounded-full bg-purple-500/20 p-1 text-purple-400">
              <Sparkles className="size-4" />
            </div>
            <div>
              <span className="font-semibold text-foreground">
                Continuity guard:
              </span>{" "}
              Catches contradictions across hundreds of pages before your readers
              do.
            </div>
          </li>
          <li
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 5 ? "opacity-100" : "opacity-40"
            )}
          >
            <div className="mt-1 shrink-0 rounded-full bg-emerald-500/20 p-1 text-emerald-400">
              <Check className="size-4" strokeWidth={3} />
            </div>
            <div>
              <span className="font-semibold text-foreground">
                You approve everything:
              </span>{" "}
              Suggestions become changes only when you say so — and never your
              prose.
            </div>
          </li>
        </ul>
      </div>
    </section>
  );
}
